import React, { useState } from 'react';
import {
  LayoutDashboard, Users, MapPin, Building2, UserCog, ClipboardList, Boxes,
  FileBarChart, Grid3x3, ShieldCheck, Settings, Upload, Plus, Download, Filter, Search, Save, Pencil,
} from 'lucide-react';
import { MobileNavGrid } from './MobileNavGrid';
import { MobileActionButton, MobileActionBar } from './MobileActionButton';
import { MobilePageHeader } from './MobilePageHeader';
import { MODULE_THEME, NAVY } from './mobileTheme';

// PREVIEW-ONLY showcase of the Phase 1 universal mobile framework. It renders the
// shared components with representative data so the standards can be reviewed
// visually. It does NOT touch any real module, route, permission or data.

const ADMIN_NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'customers', label: 'Customers', icon: Users, badge: 3 },
  { key: 'locations', label: 'Service Locations', icon: MapPin },
  { key: 'providers', label: 'Service Provider', icon: Building2 },
  { key: 'personnel', label: 'Personnel', icon: UserCog },
  { key: 'assignments', label: 'Assignments', icon: ClipboardList },
  { key: 'assets', label: 'Assets', icon: Boxes },
  { key: 'reports', label: 'Reports', icon: FileBarChart },
  { key: 'directory', label: 'Module Directory', icon: Grid3x3 },
  { key: 'users', label: 'Users & Capabilities', icon: ShieldCheck },
  { key: 'settings', label: 'System Settings', icon: Settings, soon: true },
];

const Section = ({ n, title, caption, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Standard {n}</div>
      <h2 className="text-base font-extrabold text-[#0E2A47]">{title}</h2>
      {caption && <p className="mt-0.5 text-xs text-slate-500">{caption}</p>}
    </div>
    {children}
  </section>
);

const Swatch = ({ k }) => {
  const a = MODULE_THEME[k].accent;
  return (
    <div className="flex flex-col items-center gap-2">
      <MobileActionButton icon={Plus} accent={a}>{MODULE_THEME[k].label}</MobileActionButton>
      <MobileActionButton variant="secondary" accent={a} icon={Pencil}>Edit</MobileActionButton>
    </div>
  );
};

const MobileUIKit = () => {
  const [active, setActive] = useState('overview');
  return (
    <div className="min-h-[100dvh] bg-[#F5F6F8]">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-white/10 px-4 py-3" style={{ backgroundColor: NAVY }}>
        <img src="/total_e_icon.webp" alt="Total-E" className="h-8 w-8 rounded-lg object-contain" />
        <div>
          <div className="text-sm font-extrabold leading-none text-white">Mobile UI Kit</div>
          <div className="text-[11px] text-white/70">Phase 1 — Universal mobile framework (Preview)</div>
        </div>
      </header>

      <div className="mx-auto max-w-2xl space-y-4 px-4 py-5">
        <Section n="1.1" title="Mobile Navigation Standard" caption="Directly-accessible 3-column icon grid for a module Overview. Tap a tile to see the active state.">
          <MobileNavGrid
            accent={MODULE_THEME.admin.accent}
            items={ADMIN_NAV.map((it) => ({ ...it, active: it.key === active, onClick: () => setActive(it.key) }))}
          />
        </Section>

        <Section n="1.3" title="Responsive Header + Actions" caption="Heading takes full width; Import / Add drop below in a compact bar — no horizontal overflow at 360px.">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <MobilePageHeader
              title="Service Providers"
              description="Vendor directory, qualifications and coverage."
              actions={(
                <>
                  <MobileActionButton icon={Upload} variant="secondary" accent={MODULE_THEME.admin.accent}>Import Vendors</MobileActionButton>
                  <MobileActionButton icon={Plus} accent={MODULE_THEME.admin.accent}>Add Service Provider</MobileActionButton>
                </>
              )}
            />
            <div className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400">Florida Coverage Overview & filters remain below (unchanged).</div>
          </div>
        </Section>

        <Section n="1.2" title="Universal Button Standard" caption="Compact, softly-squared, ≥44px targets. Primary uses each module accent; secondary is lightly tinted.">
          <div className="space-y-3">
            <MobileActionBar>
              <MobileActionButton icon={Save} accent={NAVY}>Save</MobileActionButton>
              <MobileActionButton icon={Filter} variant="secondary" accent={NAVY}>Filter</MobileActionButton>
              <MobileActionButton icon={Search} variant="neutral">Search</MobileActionButton>
              <MobileActionButton icon={Download} variant="secondary" accent={NAVY}>Export</MobileActionButton>
            </MobileActionBar>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
              {Object.keys(MODULE_THEME).map((k) => <Swatch key={k} k={k} />)}
            </div>
          </div>
        </Section>

        <Section n="1.4" title="Mobile Information Density" caption="Tighter banners, card padding, and gaps surface more useful data per screen while staying readable.">
          <div className="grid grid-cols-2 gap-2.5">
            {[['Property Inspections', '128'], ['Vehicle Inspections', '42'], ['Open POAs', '7'], ['Overdue Items', '3']].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{l}</div>
                <div className="mt-0.5 text-2xl font-extrabold text-[#0E2A47]">{v}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default MobileUIKit;
